import { IntlShape } from 'react-intl';
import {
  AvailabilityState,
  QuickReportResult,
  ReportingMode,
  ReportingModeValue,
} from './types';

interface Option {
  value: number;
  tooltipMessage?: string;
  orderUnavailable?: boolean;
}

// Internal clients never need a work order.
export const INTERNAL_CLIENT_NAMES = ['JIT Other', 'JIT Team'];

export const isInternalClientName = (name?: string | null) =>
  Boolean(name) && INTERNAL_CLIENT_NAMES.includes(String(name).trim());

export const unavailableOptionTooltip = 'ORDER_REPORTING.OPTION_REQUIRES_ORDER';
export const disabledOptionTooltip = 'ORDER_REPORTING.OPTION_DISABLED';
export const zeroHoursOnlyOptionTooltip =
  'ORDER_REPORTING.OPTION_ZERO_HOURS_ONLY';

// B2B and civil-contract consultants record a day without work as a 0-hour
// entry. It bills nothing, so it needs no work order in any mode: only
// entries with hours do.
export const recordsDaysWithoutWork = (contractType?: string | null) =>
  contractType === 'B2B' || contractType === 'CIVIL_CONTRACT';

// The mode that applies to one date: dates before the effective date are
// outside the rollout (OFF). null means the mode is unknown.
export const getReportingModeForDate = (
  reportingMode: ReportingMode | null | undefined,
  date?: string | null,
): ReportingModeValue | null => {
  if (!reportingMode) {
    return null;
  }
  if (
    reportingMode.mode !== 'OFF' &&
    reportingMode.effectiveDate &&
    date &&
    date.slice(0, 10) < reportingMode.effectiveDate
  ) {
    return 'OFF';
  }
  return reportingMode.mode;
};

export const isOrderRequiredOn = (
  reportingMode: ReportingMode | null | undefined,
  date?: string | null,
) => getReportingModeForDate(reportingMode, date) === 'ENFORCING';

// OFF leaves every option as it is. Options without an eligible order are
// marked unless they are exempt (absence projects). ENFORCING disables them;
// for an account that records days without work it keeps them selectable,
// because a 0-hour row needs no order. WARNING and an unknown mode mark them
// and keep them selectable.
export const decorateAvailabilityOptions = <T extends Option>(
  options: T[],
  eligibleIds: number[] | undefined,
  mode?: ReportingModeValue | null,
  isExempt: (option: T) => boolean = () => false,
  allowDaysWithoutWork = false,
): T[] => {
  if (!eligibleIds || mode === 'OFF') {
    return options;
  }
  const eligible = new Set(eligibleIds);
  return options.map((option) => {
    if (eligible.has(option.value) || isExempt(option)) {
      return option;
    }
    if (mode === 'ENFORCING' && allowDaysWithoutWork) {
      return { ...option, tooltipMessage: zeroHoursOnlyOptionTooltip };
    }
    return mode === 'ENFORCING'
      ? {
          ...option,
          orderUnavailable: true,
          tooltipMessage: disabledOptionTooltip,
        }
      : { ...option, tooltipMessage: unavailableOptionTooltip };
  });
};

export const classifyAvailability = (
  clients: { ok: boolean; status: number; data?: unknown[] },
  projects: { ok: boolean; status: number; data?: unknown[] },
): AvailabilityState['status'] => {
  if (
    [clients.status, projects.status].some(
      (status) => status === 401 || status === 403,
    )
  ) {
    return 'unauthorized';
  }
  if (clients.ok && projects.ok) {
    return clients.data?.length || projects.data?.length ? 'ready' : 'empty';
  }
  if (clients.ok || projects.ok) {
    return 'partial';
  }
  return 'error';
};

export const getBackendErrorMessage = (data: unknown) => {
  const payload = data as
    | { errorMessage?: string; message?: string }
    | undefined;
  return payload?.errorMessage || payload?.message || '';
};

export const isOrderReportingError = (data: unknown) => {
  const message = getBackendErrorMessage(data);
  return /eligible accepted assignment|one eligible assignment/i.test(message);
};

export const getReportingErrorMessage = (
  data: unknown,
  intl: IntlShape,
  fallbackId: string,
  fallbackValues?: Record<string, string | number>,
) => {
  const backendMessage = getBackendErrorMessage(data);
  if (
    /one eligible assignment for the entire reporting month/i.test(
      backendMessage,
    )
  ) {
    return intl.formatMessage({
      id: 'ORDER_REPORTING.MONTH_ENFORCEMENT_ERROR',
    });
  }
  if (isOrderReportingError(data)) {
    const reportingDate = backendMessage.match(/\b\d{4}-\d{2}-\d{2}\b/)?.[0];
    return intl.formatMessage(
      {
        id: reportingDate
          ? 'ORDER_REPORTING.ENFORCEMENT_ERROR_DATE'
          : 'ORDER_REPORTING.ENFORCEMENT_ERROR',
      },
      { date: reportingDate },
    );
  }
  return (
    backendMessage || intl.formatMessage({ id: fallbackId }, fallbackValues)
  );
};

export const formatSkippedDates = (result?: QuickReportResult) =>
  (result?.skippedDates || []).join(', ');

export const datesInRange = (start: string, end: string) => {
  const dates: string[] = [];
  const current = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (!Number.isNaN(current.getTime()) && current <= last) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return dates;
};
